import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, KeyRound, ShieldCheck, Sparkles } from "lucide-react";
import { Fade, MaskedLines, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import CityGrid from "@/components/CityGrid";
import EmptyState from "@/components/EmptyState";
import SearchBar from "@/components/SearchBar";
import ProjectCard from "@/components/ProjectCard";
import { IMG, SITE } from "@/lib/site";
import { getCity } from "@/data/cities";
import { countByCategory, publicProjects } from "@/data/projects";
import { useApproxCity } from "@/lib/geo";

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

    return (
        <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden">
            <motion.div style={{ y }} className="absolute inset-0 scale-110">
                <img
                    src={IMG.hero_residential}
                    alt="Luxury residential architecture in India"
                    className="h-full w-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-obsidian/25" />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(100deg, rgba(250,248,244,0.97) 0%, rgba(250,248,244,0.82) 45%, rgba(250,248,244,0.3) 78%)",
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
            <motion.div
                style={{ opacity }}
                className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8"
            >
                <Fade delay={0.1}>
                    <p className="eyebrow" data-testid="hero-eyebrow">
                        Sthaan · New-Project Discovery for India
                    </p>
                </Fade>
                <h1
                    data-testid="hero-headline"
                    className="mt-6 font-serif text-4xl font-light leading-[1.06] tracking-tight text-ivory sm:text-6xl lg:text-7xl"
                >
                    <MaskedLines
                        lines={[
                            "Find your next space.",
                            "Direct from builders.",
                            "Zero brokerage.",
                        ]}
                    />
                </h1>
                <Fade delay={0.9}>
                    <p className="mt-7 max-w-xl text-sm leading-relaxed text-mist sm:text-base">
                        Find villas, apartments, penthouses, plots and commercial spaces —
                        newly launched, under construction or ready to move. Sthaan is a
                        curated showcase of new projects across India, presented beautifully
                        and connected directly to the developers behind them. No brokers. No
                        classifieds. No noise.
                    </p>
                </Fade>
                <Fade delay={1.1}>
                    <div
                        data-testid="hero-search-form"
                        className="glass mt-10 max-w-2xl rounded-3xl p-3 sm:rounded-full"
                    >
                        <SearchBar
                            inputTestId="hero-search-input"
                            submitTestId="hero-search-submit"
                            placeholder="Search projects, localities, cities"
                        />
                    </div>
                </Fade>
                <Fade delay={1.3}>
                    <div className="mt-10 flex flex-wrap gap-2.5">
                        {["Residential", "Commercial", "Pan-India", "Direct to Developer"].map(
                            (chip) => (
                                <span
                                    key={chip}
                                    className="rounded-full border border-black/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mist"
                                >
                                    {chip}
                                </span>
                            ),
                        )}
                    </div>
                </Fade>
            </motion.div>
            <Fade delay={1.7} className="absolute bottom-6 left-1/2 -translate-x-1/2">
                <ArrowDown className="h-5 w-5 animate-bounce text-gold/70" />
            </Fade>
        </section>
    );
}

function AroundYou() {
    const { loading, detected, slug } = useApproxCity();
    if (loading || (!detected && !slug)) return null;
    const city = slug ? getCity(slug) : null;
    const cityHasProjects = city ? publicProjects.some((p) => p.citySlug === city.slug) : false;
    return (
        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="around-you">
            <Reveal>
                <div className="glass gold-ring flex flex-col items-start justify-between gap-6 rounded-3xl p-8 sm:flex-row sm:items-center sm:p-10">
                    <div>
                        <p className="eyebrow">Around You</p>
                        <h2 className="mt-3 font-serif text-2xl text-ivory sm:text-3xl">
                            {city
                                ? `We think you're near ${city.name}.`
                                : `We think you're near ${detected}.`}
                        </h2>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-mist">
                            {cityHasProjects
                                ? `New projects are live in ${city.name} — explore them before anyone else.`
                                : `We're currently onboarding verified developers in ${
                                      city ? city.name : detected
                                  }. Until then, explore what Sthaan is showcasing across India.`}
                        </p>
                    </div>
                    <Link
                        to={city ? `/city/${city.slug}` : "/discover"}
                        data-testid="around-you-cta"
                        className="inline-flex shrink-0 items-center gap-2 rounded-full border border-gold/50 px-6 py-3 text-sm font-medium text-gold transition-colors hover:bg-gold/10"
                    >
                        {city ? `View ${city.name}` : "Explore India"}
                        <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </Reveal>
        </section>
    );
}

const CATEGORY_BANDS = [
    {
        id: "residential",
        title: "Residential",
        image: IMG.residential_tower,
        copy: "Newly launched towers, villas and plotted communities — from sea-facing residences to garden townships.",
        to: "/residential",
    },
    {
        id: "commercial",
        title: "Commercial",
        image: IMG.commercial_office,
        copy: "Grade-A offices, boutique workspaces and high-street retail — discovered at launch, directly from developers.",
        to: "/commercial",
    },
];

function CategoryBands() {
    return (
        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
            <Reveal>
                <p className="eyebrow">Two Worlds of New</p>
                <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
                    Every listing is new. Every connection is direct.
                </h2>
            </Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-2">
                {CATEGORY_BANDS.map((band, i) => {
                    const count = countByCategory(band.id);
                    return (
                        <Reveal key={band.id} delay={i * 0.12}>
                            <Link
                                to={band.to}
                                data-testid={`home-band-${band.id}`}
                                className="group relative block h-[26rem] overflow-hidden rounded-3xl"
                            >
                                <img
                                    src={band.image}
                                    alt={`${band.title} architecture`}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-transparent" />
                                <span className="absolute left-6 top-6 rounded-full bg-obsidian/80 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur">
                                    {count > 0
                                        ? `${count} live project${count > 1 ? "s" : ""}`
                                        : "Onboarding verified developers"}
                                </span>
                                <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
                                    <h3 className="font-serif text-4xl text-ivory">{band.title}</h3>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-mist">
                                        {band.copy}
                                    </p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-gold">
                                        Explore {band.title.toLowerCase()}
                                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    );
                })}
            </div>
        </section>
    );
}

function FeaturedProjects() {
    return (
        <section className="border-y border-black/5 bg-surface/50">
            <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="eyebrow">Now Showcasing</p>
                            <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
                                Live projects, direct from the builder
                            </h2>
                        </div>
                        <Link
                            to="/discover"
                            data-testid="home-view-all-projects"
                            className="link-line text-sm font-medium text-gold"
                        >
                            View all projects
                        </Link>
                    </div>
                </Reveal>
                <div
                    className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
                    data-testid="home-featured-projects"
                >
                    {publicProjects.slice(0, 6).map((p, i) => (
                        <ProjectCard key={p.slug} project={p} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

const STEPS = [
    {
        icon: Sparkles,
        title: "Discover",
        text: "Browse a small, curated collection of genuine new projects — by city, locality or developer. Never resale, never rentals.",
    },
    {
        icon: KeyRound,
        title: "Explore the showcase",
        text: "Each project gets its own crafted page — photography, plans, amenities and story, built only from developer-supplied material.",
    },
    {
        icon: ShieldCheck,
        title: "Connect directly",
        text: "One tap opens WhatsApp straight to the developer, with your enquiry pre-filled. No accounts, no brokers, no brokerage.",
    },
];

function HowItWorks() {
    return (
        <section className="border-y border-black/5 bg-surface/40">
            <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                <Reveal>
                    <p className="eyebrow">How Sthaan Works</p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                        Discovery to conversation in three movements.
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-black/8 bg-black/5 md:grid-cols-3">
                    {STEPS.map((step, i) => (
                        <div key={step.title} className="group bg-card p-8 transition-colors duration-300 hover:bg-surface sm:p-10">
                            <div className="flex items-center justify-between">
                                <step.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                <span className="font-mono text-xs text-dim">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h3 className="mt-6 font-serif text-2xl text-ivory">{step.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-mist">{step.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BuyerBand() {
    return (
        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
            <Reveal>
                <div className="gold-ring relative overflow-hidden rounded-3xl bg-surface/60 p-10 sm:p-16">
                    <div
                        className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full opacity-15 blur-3xl"
                        style={{ background: "radial-gradient(circle, #C96F4A 0%, transparent 65%)" }}
                    />
                    <div className="relative grid items-center gap-10 lg:grid-cols-2">
                        <div>
                            <p className="eyebrow">For Buyers</p>
                            <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                                Can't find your sthaan yet?
                            </h2>
                            <p className="mt-5 max-w-lg text-sm leading-relaxed text-mist">
                                We're opening city by city with a small, verified collection.
                                Tell us what you're looking for — city, configuration,
                                anything — and we'll let you know the moment a matching
                                showcase goes live.
                            </p>
                        </div>
                        <div className="flex flex-col items-start gap-4 lg:items-end">
                            <a
                                href={`mailto:${SITE.email}?subject=${encodeURIComponent("I'm looking for a project")}`}
                                data-testid="home-buyers-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-gold-bright"
                            >
                                Write to us
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                            <Link
                                to="/discover"
                                data-testid="home-buyers-browse"
                                className="link-line text-sm text-mist transition-colors hover:text-gold"
                            >
                                Browse what's live
                            </Link>
                        </div>
                    </div>
                </div>
            </Reveal>
        </section>
    );
}

export default function Home() {
    return (
        <div data-testid="home-page">
            <Hero />
            <Marquee />
            <AroundYou />
            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="eyebrow">City by City</p>
                            <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
                                Discover by city
                            </h2>
                        </div>
                        <p className="max-w-sm text-sm leading-relaxed text-mist">
                            Sthaan opens one city at a time — each with a hand-picked set of
                            verified new projects.
                        </p>
                    </div>
                </Reveal>
                <div className="mt-12">
                    <CityGrid />
                </div>
            </section>
            {publicProjects.length > 0 ? (
                <FeaturedProjects />
            ) : (
                <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
                    <EmptyState compact />
                </section>
            )}
            <CategoryBands />
            <HowItWorks />
            <BuyerBand />
        </div>
    );
}
